import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import GoBackButton from '@/common/components/GoBackButton'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import SelectController from '@/common/components/SelectController'
import TextField from '@/common/components/TextField'
import { useProducts } from '@/common/hooks/queries/useProducts'
import useDebounce from '@/common/hooks/useDebounce'
import { useGoBack } from '@/common/hooks/useGoBack'
import useUserPermissions from '@/common/hooks/useUserPermissions'
import { Mapper } from '@/common/services/mapper'
import { FormTransformers } from '@/common/utils/formTransformers'
import { useProductNfceMapping } from '@/features/productNfceMappings/hooks/useProductNfceMapping'
import { useUpdateProductNfceMapping } from '@/features/productNfceMappings/hooks/useUpdateProductNfceMapping'
import { createSchema, ProductNfceMappingValidationSchema } from '@/features/productNfceMappings/schemas/create.schema'
import { ProductData } from '@/features/products/types/product'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { ChevronsRight } from 'lucide-react'
import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { Link, useParams } from 'react-router-dom'

const Update = () => {
  const { id } = useParams()
  const queryClient = useQueryClient()
  const goBack = useGoBack()

  const [messages, setMessages] = useState<string[] | boolean>(false)
  const [searchProduct, setSearchProduct] = useDebounce('')

  const {
    data: initialProductNfceMapping,
    isPending: initialIsPending,
    error: initialError = {} as any,
    refetch: refetchProductNfceMapping,
  } = useProductNfceMapping(id || '')

  const {
    register,
    handleSubmit,
    control,
    setError,
    reset,
    formState: { errors },
  } = useForm<ProductNfceMappingValidationSchema>({
    resolver: zodResolver(createSchema),
  })

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()
  const hasUpdatePermission = !isUserPermissionsPending ? hasUserPermission('Admin::ProductNfceMappings::Update') : true

  const { data: productOptions } = useProducts<{ label: string; value: string }[]>({
    filters: { search: searchProduct },
    mapper: Mapper.mapToOptions<ProductData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  })

  const { mutate: updateProductNfceMapping, isPending: updateIsPending } = useUpdateProductNfceMapping({
    productNfceMappingId: id || '',
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['productNfceMappings'] })
      queryClient.invalidateQueries({ queryKey: ['productNfceMapping', id] })
      goBack({ fallback: `/product-nfce-mappings/${id}` })
    },
    onError: FormTransformers.errorsTransformer({ setError, setMessages }),
  })

  const onSubmit: SubmitHandler<ProductNfceMappingValidationSchema> = (data) => updateProductNfceMapping(data)

  useEffect(() => {
    if (initialIsPending) return

    reset({
      name: initialProductNfceMapping?.name,
      product_id: {
        value: initialProductNfceMapping?.product?.id || '',
        label: initialProductNfceMapping?.product?.display_name || '',
      },
    })
  }, [initialIsPending])

  return (
    <ErrorMessageWrapper className="h-[calc(100vh-128px)]" statusCode={hasUpdatePermission ? 200 : 403}>
      <form onSubmit={handleSubmit(onSubmit)} autoComplete="false">
        <div className="flex justify-between items-center mb-6">
          <div className="flex gap-2 items-center">
            <GoBackButton fallback={`/product-nfce-mappings/${id}`} />

            <p className="text-white-400 font-normal">Mapeamento de Produtos NFCe</p>

            <ChevronsRight color="#ffffffb3" />

            <h2>{initialProductNfceMapping?.name}</h2>
          </div>

          <div className="flex gap-4">
            <Link to={`/product-nfce-mappings/${id}`}>
              <Button variant="outline-error">Cancelar</Button>
            </Link>

            <Button
              data-testid="update_product_nfce_mapping_button"
              type="submit"
              className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
              isLoading={updateIsPending}
            >
              Confirmar
            </Button>
          </div>
        </div>

        {initialIsPending ? (
          <Box>Carregando...</Box>
        ) : (
          <ErrorMessageWrapper
            className="max-h-[calc(100vh-128px)]"
            statusCode={initialError?.status}
            refetchFunction={refetchProductNfceMapping}
          >
            <Box className="flex max-h-[calc(100vh-128px)] flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] overflow-y-auto">
              <p className="text-lg font-bold">Editar mapeamento</p>

              <div className="flex gap-2">
                <TextField
                  label="Nome"
                  className="w-1/2"
                  markAsRequired
                  errorMessage={errors?.name?.message || undefined}
                  {...register('name')}
                  disabled={updateIsPending}
                />
                <div className="w-1/2">
                  <SelectController
                    control={control}
                    name="product_id"
                    label="Produto"
                    markAsRequired
                    onInputChange={setSearchProduct}
                    options={productOptions || []}
                    errorMessage={errors?.product_id?.message}
                  />
                </div>
              </div>
            </Box>
          </ErrorMessageWrapper>
        )}

        <ModalMessageSimple messages={messages} setMessages={setMessages} isError />
      </form>
    </ErrorMessageWrapper>
  )
}

export default Update
